import { useState, useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Grid, InputAdornment, OutlinedInput, Zoom, SvgIcon, Button, Modal, Typography, Box } from "@material-ui/core";
import RebaseTimer from "../../components/RebaseTimer";
import { trim } from "../../helpers";
import { changeStake, changeApproval } from "../../store/slices/stake-thunk";
import "./stake.scss";
import { useWeb3Context } from "../../hooks";
import { IPendingTxn, isPendingTxn, txnButtonText } from "../../store/slices/pending-txns-slice";
import { Skeleton } from "@material-ui/lab";
import { IReduxState } from "../../store/slices/state.interface";
import { messages } from "../../constants/messages";
import classnames from "classnames";
import { warning } from "../../store/slices/messages-slice";
import { ReactComponent as Diamond } from "../../assets/icons/diamond.svg";
import { isWhiteSpaceLike } from "typescript";
import { loadAppDetails } from "../../store/slices/app-slice";

function Stake() {
    const dispatch = useDispatch();
    const { provider, address, connect, chainID, checkWrongNetwork } = useWeb3Context();

    const [view, setView] = useState(0);
    const [quantity, setQuantity] = useState<string>("");

    const isAppLoading = useSelector<IReduxState, boolean>(state => state.app.loading);
    const currentIndex = useSelector<IReduxState, string>(state => {
        return state.app.currentIndex;
    });
    const fiveDayRate = useSelector<IReduxState, number>(state => {
        return state.app.fiveDayRate;
    });
    const dbBalance = useSelector<IReduxState, string>(state => {
        return state.account.balances && state.account.balances.db;
    });
    const sdbBalance = useSelector<IReduxState, string>(state => {
        return state.account.balances && state.account.balances.sdb;
    });
    const stakeAllowance = useSelector<IReduxState, number>(state => {
        return state.account.staking && state.account.staking.db;
    });
    const unstakeAllowance = useSelector<IReduxState, number>(state => {
        return state.account.staking && state.account.staking.sdb;
    });
    const stakingRebase = useSelector<IReduxState, number>(state => {
        return state.app.stakingRebase;
    });
    const marketprice = useSelector<IReduxState, number>(state => {
        return state.app.marketPrice;
    });
    const stakingAPY = useSelector<IReduxState, number>(state => {
        return state.app.stakingAPY;
    });
    // const warmupinfo = useSelector<IReduxState, number>(state => {
    //     return state.app.warmup;
    // });

    // const warmupbalance = useSelector<IReduxState, number>(state => {
    //     return state.app.warmupbalanceof;
    // });
    const stakingTVL = useSelector<IReduxState, number>(state => {
        return state.app.stakingTVL;
    });

    const pendingTransactions = useSelector<IReduxState, IPendingTxn[]>(state => {
        return state.pendingTransactions;
    });

    const setMax = () => {
        if (view === 0) {
            setQuantity(dbBalance);
        } else {
            setQuantity(sdbBalance);
        }
    };

    const onSeekApproval = async (token: string) => {
        if (await checkWrongNetwork()) return;

        await dispatch(changeApproval({ address, token, provider, networkID: chainID }));
    };

    const onChangeStake = async (action: string) => {
        if (await checkWrongNetwork()) return;
        if (quantity === "" || parseFloat(quantity) === 0) {
            dispatch(warning({ text: action === "stake" ? messages.before_stake : messages.before_unstake }));
        } else {
            await dispatch(changeStake({ address, action, value: String(quantity), provider, networkID: chainID }));
            await dispatch(loadAppDetails({ networkID: chainID, provider: provider }));
            setQuantity("");
        }
    };

    // const onExitWarmUp = async (action: string) => {
    //     if (await checkWrongNetwork()) return;
    //     handleClose();
    //     await dispatch(changeStake({ address, action, value: String(quantity), provider, networkID: chainID }));
    //     // await dispatch(loadAppDetails({ networkID: chainID, provider: provider }));
    // };

    // const onClaimWarmUp = async (action: string) => {
    //     if (await checkWrongNetwork()) return;
    //     handleClose();
    //     await dispatch(changeStake({ address, action, value: String(quantity), provider, networkID: chainID }));
    //     await dispatch(loadAppDetails({ networkID: chainID, provider: provider }));
    // };
    const hasAllowance = useCallback(
        token => {
            if (token === "db") return stakeAllowance > 0;
            if (token === "sdb") return unstakeAllowance > 0;
            return 0;
        },
        [stakeAllowance],
    );

    const changeView = (newView: number) => () => {
        setView(newView);
        setQuantity("");
    };
    const style = {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: 500,
        bgcolor: "black",
        border: "2px solid #0096FF",
        boxShadow: 24,
        p: 4,
    };

    // const [open, setOpen] = useState(false);
    // const handleOpens = () => setOpen(true);
    // const handleClose = () => setOpen(false);

    const trimmedSdbBalance = trim(Number(sdbBalance), 6);
    // const warmupnumber = trim(Number(warmupinfo));
    const trimmedStakingAPY = trim(stakingAPY * 100, 1);
    const stakingRebasePercentage = trim(stakingRebase * 100, 4);
    const nextRewardValue = trim((Number(stakingRebasePercentage) / 100) * Number(trimmedSdbBalance), 6);

    return (
        <div className="stake-view">
            <Zoom in={true}>
                <div className="stake-card">
                    <Grid className="stake-card-grid" container direction="column" spacing={2}>
                        <Grid item>
                            <div className="stake-card-header">
                                <p className="stake-card-header-title">
                                    DB Staking (<SvgIcon component={Diamond} />
                                    ,<SvgIcon component={Diamond} />)
                                </p>
                                <RebaseTimer />
                            </div>
                        </Grid>

                        <Grid item>
                            <div className="stake-card-metrics">
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4} md={4} lg={4}>
                                        <div className="stake-card-apy">
                                            <p className="stake-card-metrics-title">APY</p>
                                            <p className="stake-card-metrics-value APYvalue">
                                                {stakingAPY ? (
                                                    <>
                                                        {trimmedStakingAPY.length > 3
                                                            ? new Intl.NumberFormat().format(Math.round(Number(trimmedStakingAPY)))
                                                            : Number.parseFloat(trimmedStakingAPY).toPrecision(10)}
                                                        %
                                                    </>
                                                ) : (
                                                    <Skeleton width="150px" />
                                                )}
                                            </p>
                                        </div>
                                    </Grid>

                                    <Grid item xs={6} sm={4} md={4} lg={4}>
                                        <div className="stake-card-tvl">
                                            <p className="stake-card-metrics-title">TVL</p>
                                            <p className="stake-card-metrics-value">
                                                {stakingTVL ? (
                                                    new Intl.NumberFormat("en-US", {
                                                        style: "currency",
                                                        currency: "USD",
                                                        maximumFractionDigits: 0,
                                                        minimumFractionDigits: 0,
                                                    }).format(stakingTVL)
                                                ) : (
                                                    <Skeleton width="150px" />
                                                )}
                                            </p>
                                        </div>
                                    </Grid>

                                    <Grid item xs={6} sm={4} md={4} lg={4}>
                                        <div className="stake-card-index">
                                            <p className="stake-card-metrics-title">Current Index</p>
                                            <p className="stake-card-metrics-value">{currentIndex ? <>{trim(Number(currentIndex), 2)} DB</> : <Skeleton width="150px" />}</p>
                                        </div>
                                    </Grid>
                                </Grid>
                            </div>
                        </Grid>

                        <div className="stake-card-area">
                            {!address && (
                                <div className="stake-card-wallet-notification">
                                    <div className="stake-card-wallet-connect-btn" onClick={connect}>
                                        <p>Connect Wallet</p>
                                    </div>
                                    <p className="stake-card-wallet-desc-text">Connect your wallet to stake DB tokens!</p>
                                </div>
                            )}
                            {address && (
                                <div>
                                    <div className="stake-card-action-area">
                                        <div className="stake-card-action-stage-btns-wrap">
                                            <div onClick={changeView(0)} className={classnames("stake-card-action-stage-btn", { active: !view })}>
                                                <p>Stake</p>
                                            </div>
                                            <div onClick={changeView(1)} className={classnames("stake-card-action-stage-btn", { active: view })}>
                                                <p>Unstake</p>
                                            </div>
                                        </div>

                                        <div className="stake-card-action-row">
                                            <OutlinedInput
                                                type="number"
                                                placeholder="Amount"
                                                className="stake-card-action-input"
                                                value={quantity}
                                                onChange={e => setQuantity(e.target.value)}
                                                labelWidth={0}
                                                endAdornment={
                                                    <InputAdornment position="end">
                                                        <div onClick={setMax} className="stake-card-action-input-btn">
                                                            <p>Max</p>
                                                        </div>
                                                    </InputAdornment>
                                                }
                                            />

                                            {view === 0 && (
                                                <div className="stake-card-tab-panel">
                                                    {address && hasAllowance("db") ? (
                                                        <div
                                                            className="stake-card-tab-panel-btn"
                                                            onClick={() => {
                                                                if (isPendingTxn(pendingTransactions, "staking")) return;
                                                                onChangeStake("stake");
                                                            }}
                                                        >
                                                            <p>{txnButtonText(pendingTransactions, "staking", "Stake DB")}</p>
                                                        </div>
                                                    ) : (
                                                        <div
                                                            className="stake-card-tab-panel-btn"
                                                            onClick={() => {
                                                                if (isPendingTxn(pendingTransactions, "approve_staking")) return;
                                                                onSeekApproval("db");
                                                            }}
                                                        >
                                                            <p>{txnButtonText(pendingTransactions, "approve_staking", "Approve")}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {view === 1 && (
                                                <div className="stake-card-tab-panel">
                                                    {address && hasAllowance("sdb") ? (
                                                        <div
                                                            className="stake-card-tab-panel-btn"
                                                            onClick={() => {
                                                                if (isPendingTxn(pendingTransactions, "unstaking")) return;
                                                                onChangeStake("unstake");
                                                            }}
                                                        >
                                                            <p>{txnButtonText(pendingTransactions, "unstaking", "Unstake DB")}</p>
                                                        </div>
                                                    ) : (
                                                        <div
                                                            className="stake-card-tab-panel-btn"
                                                            onClick={() => {
                                                                if (isPendingTxn(pendingTransactions, "approve_unstaking")) return;
                                                                onSeekApproval("sdb");
                                                            }}
                                                        >
                                                            <p>{txnButtonText(pendingTransactions, "approve_unstaking", "Approve")}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        <div className="stake-card-action-help-text">
                                            {address && ((!hasAllowance("db") && view === 0) || (!hasAllowance("sdb") && view === 1)) && (
                                                <p>
                                                    Note: The "Approve" transaction is only needed when staking/unstaking for the first db; subsequent staking/unstaking only
                                                    requires you to perform the "Stake" or "Unstake" transaction.
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="stake-user-data">
                                        <div className="data-row">
                                            <p className="data-row-name">Your Balance</p>
                                            <p className="data-row-value">
                                                {isAppLoading ? (
                                                    <Skeleton width="80px" />
                                                ) : (
                                                    <>
                                                        {trim(Number(dbBalance), 4)} DB(
                                                        {new Intl.NumberFormat("en-US", {
                                                            style: "currency",
                                                            currency: "USD",
                                                            maximumFractionDigits: 0,
                                                            minimumFractionDigits: 0,
                                                        }).format(Number(dbBalance) * marketprice)}
                                                        )
                                                    </>
                                                )}
                                            </p>
                                        </div>
                                        {/* {isAppLoading ? (
                                            <Skeleton width="80px" />
                                        ) : (
                                            warmupbalance > 0 && (
                                                <div>
                                                    <div className="data-row">
                                                        <p className="data-row-name">Your Warm Up Balance</p>
                                                        <p className="data-row-value">
                                                            {isAppLoading ? (
                                                                <Skeleton width="80px" />
                                                            ) : (
                                                                <>
                                                                    {warmupbalance} DB(
                                                                    {new Intl.NumberFormat("en-US", {
                                                                        style: "currency",
                                                                        currency: "USD",
                                                                        maximumFractionDigits: 0,
                                                                        minimumFractionDigits: 0,
                                                                    }).format(warmupbalance * marketprice)}
                                                                    )
                                                                </>
                                                            )}
                                                        </p>
                                                    </div>
                                                    {Number(warmupnumber) > 0 ? (
                                                        <div>
                                                            <div className="data-row">
                                                                <p className="data-row-name">Pending Warm Up Till Release</p>
                                                                <p className="data-row-value">
                                                                    {isAppLoading ? <Skeleton width="80px" /> : <>{warmupnumber} rebase(s) left till claimable</>}
                                                                </p>
                                                            </div>
                                                            <div className="data-row">
                                                                <p className="data-row-name"></p>
                                                                <p className="data-row-value">
                                                                    {isAppLoading ? (
                                                                        <Skeleton width="80px" />
                                                                    ) : (
                                                                        <Button
                                                                            variant="contained"
                                                                            color="primary"
                                                                            style={{ color: "white", background: "#0096FF", borderRadius: "9px", fontWeight: 600 }}
                                                                            onClick={handleOpens}
                                                                        >
                                                                            EXIT WARM UP
                                                                        </Button>
                                                                    )}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div>
                                                            <div className="data-row">
                                                                <p className="data-row-name"></p>
                                                                <p className="data-row-value">
                                                                    <Button
                                                                        variant="contained"
                                                                        color="primary"
                                                                        style={{ color: "white", background: "#0096FF", borderRadius: "9px", fontWeight: 600 }}
                                                                        onClick={() => {
                                                                            if (isPendingTxn(pendingTransactions, "Claim")) return;
                                                                            onClaimWarmUp("Claim");
                                                                        }}
                                                                    >
                                                                        Claim
                                                                    </Button>
                                                                </p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )
                                        )} */}
                                        {/* <Modal open={open} onClose={handleClose} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
                                            <Box sx={style}>
                                                <Typography id="modal-modal-title" variant="h6" component="h2" style={{ color: "white" }}>
                                                    Are you srue you want to exit the Warm-Up?
                                                </Typography>
                                                <Typography id="modal-modal-description" style={{ color: "white" }}>
                                                    Exiting Warm-Up will return your DB balance to your account, but will not make you eligible for rebases on the current balance
                                                    in warm-up.
                                                </Typography>
                                                <div style={{ textAlign: "center", marginTop: 15 }}>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        style={{ color: "white", background: "#0096FF", borderRadius: "9px", fontWeight: 600 }}
                                                        onClick={handleClose}
                                                    >
                                                        Cancel
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        style={{ color: "white", background: "#0096FF", borderRadius: "9px", fontWeight: 600, marginLeft: "5px" }}
                                                        onClick={() => {
                                                            if (isPendingTxn(pendingTransactions, "forfeit")) return;
                                                            onExitWarmUp("forfeit");
                                                        }}
                                                    >
                                                        Confirm
                                                    </Button>
                                                </div>
                                            </Box>
                                        </Modal> */}
                                        <div className="data-row">
                                            <p className="data-row-name">Your Staked Balance</p>
                                            <p className="data-row-value">
                                                {isAppLoading ? (
                                                    <Skeleton width="80px" />
                                                ) : (
                                                    <>
                                                        {trimmedSdbBalance} sDB(
                                                        {new Intl.NumberFormat("en-US", {
                                                            style: "currency",
                                                            currency: "USD",
                                                            maximumFractionDigits: 0,
                                                            minimumFractionDigits: 0,
                                                        }).format(Number(trimmedSdbBalance) * marketprice)}
                                                        )
                                                    </>
                                                )}
                                            </p>
                                        </div>

                                        <div className="data-row">
                                            <p className="data-row-name">Next Reward Amount</p>
                                            <p className="data-row-value">
                                                {isAppLoading ? (
                                                    <Skeleton width="80px" />
                                                ) : (
                                                    <>
                                                        {nextRewardValue} sDB(
                                                        {new Intl.NumberFormat("en-US", {
                                                            style: "currency",
                                                            currency: "USD",
                                                            maximumFractionDigits: 0,
                                                            minimumFractionDigits: 0,
                                                        }).format(Number(nextRewardValue) * marketprice)}
                                                        )
                                                    </>
                                                )}
                                            </p>
                                        </div>

                                        <div className="data-row">
                                            <p className="data-row-name">Next Reward Yield</p>
                                            <p className="data-row-value">{isAppLoading ? <Skeleton width="80px" /> : <>{stakingRebasePercentage}%</>}</p>
                                        </div>

                                        <div className="data-row">
                                            <p className="data-row-name">ROI (5-Day Rate)</p>
                                            <p className="data-row-value">{isAppLoading ? <Skeleton width="80px" /> : <>{trim(Number(fiveDayRate) * 100, 4)}%</>}</p>
                                        </div>
                                        {/* <div className="data-row">
                                            <p className="note">
                                                Note: There is a 2 epoch warm-up staking period, where users must be staked for more than 2 epoch/rebase before claiming any rebase
                                                rewards. Users will accumulate rewards while in the warm-up period but will not be able to claim them for 2 epochs. When 2 epochs
                                                has elapsed your staked balance can be claimed from the warm up contract and you will automatically receive the rebase rewards
                                                thereafter.
                                            </p>
                                        </div> */}
                                    </div>
                                </div>
                            )}
                        </div>
                    </Grid>
                </div>
            </Zoom>
        </div>
    );
}

export default Stake;
